import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, TrendingDown, Users, Sparkles, Award, BookOpen, Mic, ScrollText, Play } from "lucide-react";
import { useRegion } from "../context/RegionContext";
import { SERVICES, INDUSTRIES, CASE_STUDIES, TESTIMONIALS } from "../data/site";
import { EPISODES } from "../data/episodes";
import Seo from "../components/Seo";
import TestimonialsMarquee from "../components/TestimonialsMarquee";

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.6, ease: "easeOut" } };

export default function Home() {
  const { config, region } = useRegion();
  const navigate = useNavigate();

  return (
    <div data-testid="home-page">
      <Seo
        path="/"
        title="Andrea Kanaris - Social Risk & Impact Advisory"
        description="Practitioner-led strategic advisory for corporate and government clients: Social Impact Assessment, community engagement and social risk. Serving Australia and the United States."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          name: "All Things Social Impact",
          founder: { "@type": "Person", name: "Andrea Kanaris" },
          url: "https://allthingssocialimpact.com",
          areaServed: ["Australia", "United States"],
          serviceType: ["Social Impact Assessment", "Community Engagement", "Social Risk Advisory"],
        }}
      />
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1562519990-50eb51e282b2?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt="Wind turbines in a regional landscape"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 w-full">
          <div className="lg:col-span-8 text-white">
            <div className="eyebrow text-brand-yellow mb-6">
              Advisory · Social Risk · Community Outcomes - {region === "AU" ? "Australia" : "United States"}
            </div>
            <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6 max-w-4xl">
              All Things Social Impact is a specialist social impact and social risk advisory practice led by Andrea Kanaris.
            </h1>
            <p className="text-lg lg:text-xl text-white/85 max-w-2xl leading-relaxed mb-10">
              Andrea is a senior advisor, independent reviewer and expert witness with more than 20 years' experience helping organisations identify social risk, improve project decisions and deliver stronger community outcomes.
            </p>
            <div className="flex flex-wrap gap-4">
              <button data-testid="hero-discuss-btn" onClick={() => navigate("/contact")} className="btn-primary">
                Contact Us <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/services" data-testid="hero-services-link" className="btn-secondary bg-white/10 border-white/40 text-white hover:bg-white hover:text-brand-blue">
                Explore Services
              </Link>
            </div>
            <div className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-6 max-w-3xl border-t border-white/20 pt-8">
              {[
                { k: "20+", v: "Years advising leaders" },
                { k: "40+", v: "Social Impact Assessments led or reviewed" },
                { k: "2", v: "Offices · AU + US" },
              ].map((s) => (
                <div key={s.v}>
                  <div className="font-serif text-3xl lg:text-4xl text-brand-yellow font-bold">{s.k}</div>
                  <div className="text-xs uppercase tracking-widest text-white/70 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS MARQUEE - immediate peer trust proof directly under the hero */}
      <TestimonialsMarquee />

      {/* WHAT WE DO */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <div className="eyebrow mb-4">What we do</div>
            <h2 className="font-serif text-3xl lg:text-5xl text-brand-blue leading-tight mb-6">
              Advice built for complex decisions, regulatory scrutiny and community expectations.
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-6">
              Andrea works alongside executives, project directors and government leaders to identify social risks early, sharpen the decisions that follow, and design engagement that holds up in front of regulators, lenders and the communities that live with the outcome.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Every engagement is led personally, grounded in evidence, and pitched at the level where the real trade-offs get made.
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              { icon: ShieldCheck, title: "Reduce delivery risk", body: "Surface material social risks in options and feasibility - before schedule, cost and reputation are locked in." },
              { icon: ScrollText, title: "Strengthen approvals", body: "SIA and SIMP work that satisfies regulators and lenders on first submission, not third." },
              { icon: Users, title: "Improve community outcomes", body: "Design benefits and engagement that hold up through construction and operations." },
              { icon: TrendingDown, title: "Uplift capability", body: "Executive briefings, structured workshops and coaching so your team can hold the discipline in-house." },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="border border-slate-200 p-8 hover:border-brand-orange transition-colors">
                <Icon className="w-8 h-8 text-brand-orange mb-5" />
                <h3 className="font-serif text-xl text-brand-blue mb-3">{title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="py-24 lg:py-32 bg-slate-50">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="eyebrow mb-4">Services</div>
              <h2 className="font-serif text-3xl lg:text-5xl text-brand-blue leading-tight">
                Advisory built around the decisions you have to make.
              </h2>
            </div>
            <Link to="/services" data-testid="home-view-all-services" className="link-arrow">View all services <ArrowRight className="w-4 h-4" /></Link>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.slice(0, 6).map((s, i) => (
              <motion.div key={s.slug} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.05 }}>
                <Link to={`/services/${s.slug}`} className="block h-full bg-white border border-slate-200 p-8 hover:border-brand-orange hover:shadow-md transition-all">
                  <div className="text-xs uppercase tracking-[0.2em] text-brand-orange font-semibold mb-4">0{i + 1}</div>
                  <h3 className="font-serif text-xl text-brand-blue leading-snug mb-3 min-h-[3.5rem]">{s.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-5">{s.short}</p>
                  <span className="text-sm font-semibold text-brand-blue inline-flex items-center gap-2">Learn more <ArrowRight className="w-4 h-4" /></span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-14 max-w-2xl">
            <div className="eyebrow mb-4">Industries we serve</div>
            <h2 className="font-serif text-3xl lg:text-5xl text-brand-blue leading-tight">Where our advice moves the needle.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {INDUSTRIES.map((ind) => (
              <Link to={`/industries/${ind.slug}`} key={ind.slug} data-testid={`industry-card-${ind.slug}`} className="group relative overflow-hidden h-72 block">
                <img src={ind.image} alt={ind.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-blue-deep via-brand-blue-deep/50 to-transparent" />
                <div className="relative h-full flex flex-col justify-end p-7 text-white">
                  <h3 className="font-serif text-xl mb-2 leading-snug">{ind.title}</h3>
                  <p className="text-sm text-white/80">{ind.blurb}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CASE STUDIES PREVIEW */}
      <section className="py-24 lg:py-32 bg-brand-blue text-white relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
            <div className="max-w-2xl">
              <div className="eyebrow text-brand-yellow mb-4">Selected case studies</div>
              <h2 className="font-serif text-3xl lg:text-5xl leading-tight">Specialist advice in practice.</h2>
              <p className="text-white/75 text-base lg:text-lg leading-relaxed mt-5">
                Explore how All Things Social Impact's work has informed complex project decisions, supported regulatory pathways and strengthened community outcomes.
              </p>
            </div>
            <Link to="/case-studies" className="link-arrow text-brand-yellow hover:text-white">View all case studies <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {CASE_STUDIES.slice(0, 3).map((cs) => (
              <Link to={`/case-studies/${cs.slug}`} key={cs.slug} className="block bg-brand-blue-deep border border-white/10 p-8 hover:border-brand-orange transition-colors">
                <div className="text-xs uppercase tracking-[0.2em] text-brand-yellow font-semibold mb-4">{cs.outcomeTag}</div>
                <h3 className="font-serif text-xl mb-4 leading-snug">{cs.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed mb-6">{cs.homeCardBlurb || cs.challenge}</p>
                <span className="text-sm font-semibold text-brand-yellow inline-flex items-center gap-2">Read the case study <ArrowRight className="w-4 h-4" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT ANDREA */}
      <section className="py-24 lg:py-32 bg-slate-50">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5">
            <img src="/andrea-kanaris.jpg" alt="Andrea Kanaris - Founder & Principal, All Things Social Impact" className="w-full aspect-[4/5] object-cover" />
          </div>
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4">Meet the practice</div>
            <h2 className="font-serif text-3xl lg:text-5xl text-brand-blue leading-tight mb-6">
              Andrea Kanaris - 20+ years advising on the projects that shape communities.
            </h2>
            <p className="text-slate-600 leading-relaxed text-lg mb-6">
              A leading social scientist and one of the most experienced social impact advisors working across Australia and internationally. Andrea has led or reviewed more than 40 major social impact assessments across renewables, resources and infrastructure, and is regularly engaged as an independent expert.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
              {[
                { icon: ScrollText, label: "Authored the Queensland Social Impact Assessment Guideline (2018)" },
                { icon: BookOpen, label: "Author, A Practical Guide to Social Impact Assessment for the Real World" },
                { icon: Mic, label: "Host, Voices of Change - Stories That Matter podcast" },
                { icon: Award, label: "ROAR Woman of the Year (Bronze, 2025)" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-start gap-3">
                  <Icon className="w-5 h-5 text-brand-orange flex-shrink-0 mt-1" />
                  <div className="text-sm text-slate-700 leading-relaxed">{label}</div>
                </div>
              ))}
            </div>
            <Link to="/about" className="btn-secondary" data-testid="home-about-link">Read Andrea's full biography <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-14 max-w-2xl">
            <div className="eyebrow mb-4">In their words</div>
            <h2 className="font-serif text-3xl lg:text-5xl text-brand-blue leading-tight">In the words of peers and project leaders.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.slice(0, 3).map((t) => (
              <div key={t.name} className="border border-slate-200 p-8 flex flex-col">
                <Sparkles className="w-6 h-6 text-brand-orange mb-5" />
                <blockquote className="font-serif text-lg text-brand-blue leading-relaxed mb-6 flex-1">"{t.quote}"</blockquote>
                <div className="border-t border-slate-200 pt-4">
                  <div className="font-semibold text-slate-900 text-sm">{t.name}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-widest mt-1">{t.title}</div>
                  {t.project && <div className="text-xs text-brand-orange font-semibold uppercase tracking-widest mt-2">{t.project}</div>}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/about#testimonials" className="link-arrow" data-testid="home-view-all-testimonials">Read all testimonials <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* VOICES OF CHANGE PODCAST PROMO */}
      <section data-testid="home-podcast-promo" className="py-20 bg-slate-50 border-t border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-12 items-center">
          <div className="w-full">
            <div className="aspect-video bg-black shadow-2xl">
              <iframe
                data-testid="home-podcast-player"
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${EPISODES[0].youtube_id}?rel=0`}
                title={EPISODES[0].title}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-orange mt-4">{EPISODES[0].number} · Now playing</div>
          </div>
          <div className="max-w-xl">
            <div className="eyebrow mb-4 inline-flex items-center gap-2 text-brand-orange"><Mic className="w-4 h-4" /> Voices of Change · Latest episode</div>
            <h2 className="font-serif text-3xl lg:text-5xl text-brand-blue leading-tight mb-4">{EPISODES[0].title}</h2>
            <p className="text-slate-700 text-base leading-relaxed mb-3">{EPISODES[0].excerpt}</p>
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-6">With {EPISODES[0].guest} · {EPISODES[0].duration}</div>
            <div className="flex flex-wrap gap-3">
              <Link to={`/podcast/${EPISODES[0].slug}`} className="btn-primary" data-testid="home-podcast-episode-cta">
                <Play className="w-4 h-4" /> Show notes
              </Link>
              <Link to="/podcast" className="btn-secondary" data-testid="home-podcast-cta">Explore all episodes <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* BOOK PROMO */}
      <section data-testid="home-book-promo" className="py-24 lg:py-28 bg-white border-t border-slate-100">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-14 items-center">
          <Link to="/shop/practical-guide-sia" data-testid="home-book-cover-link" className="relative w-full max-w-[340px] mx-auto lg:mx-0 group block">
            <div className="absolute -inset-3 bg-brand-yellow/20 blur-2xl -z-0" aria-hidden="true"></div>
            <img src="/practice/book-cover.webp" alt="A Practical Guide to Social Impact Assessment for the Real World by Andrea Kanaris" className="relative w-full h-auto shadow-xl group-hover:scale-[1.02] transition-transform duration-500" />
          </Link>
          <div className="max-w-2xl">
            <div className="eyebrow mb-4 inline-flex items-center gap-2 text-brand-orange"><BookOpen className="w-4 h-4" /> The book</div>
            <h2 className="font-serif text-3xl lg:text-5xl text-brand-blue leading-tight mb-4">A Practical Guide to Social Impact Assessment for the Real World.</h2>
            <p className="text-slate-700 text-base lg:text-lg leading-relaxed mb-8">
              Andrea Kanaris distills two decades of specialist advisory practice into a working reference for graduates, consultants and in-house ESG teams - scoping, baselining, engagement, impact prediction, mitigation and management planning, grounded in real projects and the standards regulators actually apply.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/shop/practical-guide-sia" className="btn-primary" data-testid="home-book-cta">Buy the book <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/shop" className="btn-secondary" data-testid="home-book-shop">Visit the shop</Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="py-24 lg:py-28 bg-brand-orange text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-serif text-3xl lg:text-5xl leading-tight mb-4">Have a project you would like to discuss?</h2>
            <p className="text-white/90 text-lg max-w-2xl">Tell us about the stage, the geography and the decision you are trying to make. We will come back within two business days.</p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <button data-testid="home-cta-discuss" onClick={() => navigate("/contact")} className="bg-white text-brand-orange px-8 py-4 font-semibold hover:bg-brand-blue hover:text-white transition-colors inline-flex items-center gap-2">
              Contact Us <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
